import { useEffect, useMemo, useRef, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, Plus, RefreshCw, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { Badge, Spinner } from '@/components/common'
import { LegRow, legLabel } from '@/components/leg-search'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { prepacksApi } from '@/services/api'
import {
  SUGGESTION_DEBOUNCE_MS,
  SUGGESTION_INITIAL_VISIBLE,
  SUGGESTION_REQUEST_LIMIT,
  SKIP_SAME_GAME_GROUP,
  addAllSelection,
  filterAddableSuggestions,
  suggestionsCoverSelection,
  suggestionRelation,
  suggestionSubtitle,
  suggestionsIdentity,
  visibleSuggestions,
} from './prepackSuggestionsModel'

const EMPTY = []

// Suggested legs sit under the builder's selection: legs from the same games
// that share a player, a side, or the matchup with what is already picked,
// and that the sportsbook combines with it. While the quote rejects the
// selection the panel pauses: nothing added could make it valid.
export default function PrepackSuggestedLegs({
  orgSlug,
  regulation,
  legs = EMPTY,
  displayLegs = EMPTY,
  enabled = true,
  maxLegs = 6,
  // The sportsbook rejects the current selection (the quote's
  // showRejectedCombination).
  rejected = false,
  onAdd,
  onAddAll,
  // Where focus goes when an add fills the selection and the panel hides.
  focusFallbackRef,
}) {
  const identity = useMemo(() => suggestionsIdentity(regulation, legs), [legs, regulation])
  // The request body travels with its identity so a debounced request never
  // pairs one selection's key with another selection's legs.
  const request = useMemo(() => ({ identity, legs }), [identity, legs])
  const debounced = useDebouncedValue(request, SUGGESTION_DEBOUNCE_MS)
  const [expandedFor, setExpandedFor] = useState(null)
  const expanded = expandedFor === regulation
  // Add buttons by outcome, and the row (or Add all) an add click is taking
  // away, so focus can move to a neighbouring control instead of dropping to
  // the page.
  const addButtons = useRef(new Map())
  const refreshButton = useRef(null)
  const pendingFocus = useRef(null)

  const visible = Boolean(enabled && orgSlug && regulation && legs.length > 0 && legs.length < maxLegs)
  const query = useQuery({
    queryKey: ['prepacks', 'outcome-suggestions', orgSlug, debounced.identity],
    queryFn: ({ signal }) => prepacksApi.suggestOutcomes(
      orgSlug,
      { regulation, legs: debounced.legs, limit: SUGGESTION_REQUEST_LIMIT },
      { signal },
    )
      // The selection this answer is about (suggestionsCoverSelection).
      .then((response) => ({ ...response, requested: debounced.legs.map((leg) => String(leg.outcome_id)) })),
    enabled: visible && !rejected && debounced.identity === identity,
    retry: false,
    staleTime: 30000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  })

  // A leg added from search since the last answer is one no row's
  // `conflicts` can name, so the rows wait for the answer that knows it.
  const covered = suggestionsCoverSelection(query.data, legs)
  const addable = useMemo(
    () => (covered ? filterAddableSuggestions(query.data?.suggestions || EMPTY, legs) : EMPTY),
    [covered, legs, query.data?.suggestions],
  )
  const shown = visibleSuggestions(addable, expanded)

  // Runs before the early return below so it also sees the render in which
  // an add fills the selection and the panel disappears.
  useEffect(() => {
    const pending = pendingFocus.current
    if (!pending) return
    pendingFocus.current = null
    if (!visible) {
      focusFallbackRef?.current?.focus()
      return
    }
    if (pending.all) {
      // Add all stays focused unless nothing is left for it to add.
      if (shown.length === 0) refreshButton.current?.focus()
      return
    }
    // A rejected add leaves the row, and focus, where they were.
    if (shown.some((suggestion) => suggestion.leg.outcome_id === pending.outcomeId)) return
    const target = shown[pending.index] || shown[pending.index - 1]
    const button = target ? addButtons.current.get(target.leg.outcome_id) : refreshButton.current
    button?.focus()
  })

  if (!visible) return null

  const warnings = query.data?.warnings || EMPTY
  // No answer yet, or none for a leg added since: shown as the first load.
  const waiting = !covered && !query.isError
  const refreshing = !rejected && !waiting && query.isFetching && Boolean(query.data)

  return (
    <div
      className="mb-3 mt-3 rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] p-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-[var(--wf-color-text)]">
            <Sparkles className="h-3.5 w-3.5 text-[var(--prepacks-accent-text)]" aria-hidden="true" />
            Suggested legs
            {refreshing ? <Spinner size="compact" label="Refreshing suggestions" /> : null}
          </p>
          <p className="mt-0.5 truncate text-[10px] text-[var(--prepacks-muted)]">{suggestionSubtitle(displayLegs)}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            disabled={rejected || shown.length === 0}
            onClick={() => {
              pendingFocus.current = { all: true }
              const { legs: all, skipped } = addAllSelection(shown)
              for (const { suggestion, because, reason } of skipped) {
                toast.error(reason === SKIP_SAME_GAME_GROUP
                  ? `Skipped ${legLabel(suggestion.leg)}: a pack can hold one same-game group, and ${legLabel(because.leg)} already starts one`
                  : `Skipped ${legLabel(suggestion.leg)}: the sportsbook won't combine it with ${legLabel(because.leg)}`)
              }
              onAddAll?.(all)
            }}
            className="rounded px-2 py-1 text-[11px] font-semibold text-[var(--prepacks-accent-text)] hover:underline disabled:pointer-events-none disabled:opacity-40"
          >
            Add all
          </button>
          {rejected ? null : (
            <button
              ref={refreshButton}
              type="button"
              aria-label="Refresh suggestions"
              // Joins a fetch already in flight rather than restarting it; the
              // header spinner shows the refresh.
              onClick={() => void query.refetch({ cancelRefetch: false })}
              className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-semibold text-[var(--prepacks-muted)] hover:text-[var(--wf-color-text)]"
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              Refresh
            </button>
          )}
        </div>
      </div>

      {rejected ? (
        <p role="status" className="mt-2 text-xs text-[var(--prepacks-muted)]">
          Change or remove the rejected leg before adding more. Suggestions resume once the sportsbook accepts the selection.
        </p>
      ) : waiting ? (
        <p role="status" className="mt-2 text-xs text-[var(--prepacks-muted)]">Finding related legs…</p>
      ) : query.isError ? (
        <div
          role="alert"
          className="mt-2 rounded border border-[var(--wf-color-danger-border)] bg-[var(--wf-color-danger-soft)] p-2 text-xs text-[var(--wf-color-danger)]"
        >
          <p className="font-semibold">Suggestions are unavailable.</p>
          {query.error?.response?.data?.error ? <p className="mt-1">{query.error.response.data.error}</p> : null}
          <button
            type="button"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
            className="mt-2 rounded border border-[var(--wf-color-danger-border)] px-2 py-1 font-semibold hover:bg-[var(--wf-color-danger-soft)] disabled:opacity-50"
          >
            Retry
          </button>
        </div>
      ) : addable.length === 0 ? (
        <p className="mt-2 text-xs text-[var(--prepacks-muted)]">No related legs can be combined with this selection.</p>
      ) : (
        <ul aria-label="Suggested legs" className="mt-2 flex flex-col gap-1.5">
          {shown.map((suggestion, index) => {
            const { leg } = suggestion
            const relation = suggestionRelation(suggestion.relation)
            return (
              <li
                key={leg.outcome_id}
                className="rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-3 py-2"
              >
                <LegRow
                  compact
                  leg={leg}
                  badge={<Badge compact tone={relation.tone}>{relation.label}</Badge>}
                  actions={(
                    <button
                      ref={(node) => {
                        if (node) addButtons.current.set(leg.outcome_id, node)
                        else addButtons.current.delete(leg.outcome_id)
                      }}
                      type="button"
                      aria-label={`Add ${legLabel(leg)}`}
                      onClick={() => {
                        pendingFocus.current = { outcomeId: leg.outcome_id, index }
                        onAdd?.(leg)
                      }}
                      className="flex items-center gap-0.5 rounded border border-[var(--prepacks-border)] px-2 py-1 text-[11px] font-semibold text-[var(--wf-color-text)] hover:border-[var(--wf-color-action-text)]"
                    >
                      <Plus className="h-3 w-3" aria-hidden="true" />
                      Add
                    </button>
                  )}
                />
              </li>
            )
          })}
        </ul>
      )}

      {!rejected && !waiting && !query.isError && warnings.length > 0 ? (
        <div className="mt-2 flex flex-col gap-0.5">
          {warnings.map((warning) => (
            <p key={warning} className="text-[10px] text-[var(--prepacks-muted)]">{warning}</p>
          ))}
        </div>
      ) : null}

      {!rejected && addable.length > SUGGESTION_INITIAL_VISIBLE && !query.isError ? (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpandedFor(expanded ? null : regulation)}
          className="mt-2 flex w-full items-center justify-center gap-1 text-[11px] font-semibold text-[var(--prepacks-accent-text)] hover:underline"
        >
          {expanded ? (
            <>Show fewer <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" /></>
          ) : (
            <>See more suggestions <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /></>
          )}
        </button>
      ) : null}
    </div>
  )
}
